import { FormControlLabel, Grid2, MenuItem, Switch } from "@mui/material";
import { useEffect, useState } from "react";
import { SelectValidator, TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { useNavigate, useParams } from "react-router-dom";
import { DataTypeEnum } from "../../../_common/enum/dataType.enum";
import { FormFieldKeyConditionEnum } from "../../../_common/enum/forms/fomFieldKeyCondition.enum";
import { FormFieldKeyConditionTypeEnum } from "../../../_common/enum/forms/fomFieldKeyConditionType.enum";
import { FormTemplateSectionTypeEnum } from "../../../_common/enum/forms/formTemplateSectionType.enum";
import { IFormTemplateSectionManage } from "../../../_common/models/formTemplateSection";
import { DataTypeAutoComplete } from "../../../_components/autocomplete/dataType.autocomplete";
import EnumSelect from "../../../_components/autocomplete/enum.select";
import { StandardButton } from "../../../_components/button/primary.button";
import { StandardTextValidator } from "../../../_components/textValidator/standardTextValidator";
import { toastr } from "../../../_components/toastr/toastr";
import { formTemplateSectionService } from "../../../_services/formTemplateSection.service";

export function FormTemplateSectionManage(params: {
    emitOnSave: () => void,
}) {

    const [saving, setIsSaving] = useState(false);
    const [isLoading, setLoading] = useState(false);
    let navigate = useNavigate();

    const { formTemplateId, formTemplateSectionId } = useParams<{ formTemplateId: string, formTemplateSectionId: string }>();
    const [data, setData] = useState<IFormTemplateSectionManage & { hasFormFieldConditionDependency?: boolean, formFieldKeyConditionDependencies?: Array<any>, formFieldKeyConditionTypeId?: number }>({
        id: +(formTemplateSectionId || 0),
        name: "",
        sectionOrder: null,
        formHtml: "",
        formTemplateId: +(formTemplateId || 0),
        formTemplateSectionTypeId: "",
        hasFormFieldConditionDependency: false,
        formFieldKeyConditionDependencies: [],
        formFieldKeyConditionTypeId: undefined
    });

    useEffect(() => {
        if (+(formTemplateSectionId || 0) > 0) fetchData();

    }, [formTemplateSectionId]);

    useEffect(() => {
        if (data.hasFormFieldConditionDependency === true && (!data.formFieldKeyConditionDependencies || !data.formFieldKeyConditionDependencies.length)) {
            setData((_data: any) => ({ ..._data, formFieldKeyConditionDependencies: [{ formFieldKeyId: null, formTemplateSectionId: 0, conditionId: 0, value: "" }] }))
        }
    }, [data.hasFormFieldConditionDependency]);

    function fetchData() {
        setLoading(true);
        return formTemplateSectionService
            .get(+(formTemplateSectionId || 0))
            .then(
                (json) => {
                    setData(json)
                }
            )
            .finally(() => setLoading(false));
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);

        return formTemplateSectionService
            .submit(data)
            .then(
                () => {
                    toastr.success("Created successfully");
                    params.emitOnSave();
                    navigate(-1);
                },
                (e) => {
                    console.log(e);
                    toastr.error("Please verify that the form is valid");
                }
            )
            .finally(() => setIsSaving(false));
    }

    function handleChange(event: any) {
        setData((__data: any) => { return { ...__data, [event.target.name]: event.target.value } });
    };

    function handleChangeSwitch(event: any) {
        setData((__data: any) => { return { ...__data, [event.target.name]: !__data[event.target.name] } });
    };

    function onError(event: any) {
        toastr.error("Please verify that all fields are correct");
    }

    function disable() {
        setIsSaving(true);

        return formTemplateSectionService
            .disable(data.id)
            .then(
                () => {
                    toastr.success("Closed successfully");
                    params.emitOnSave();
                }
            )
            .finally(() => setIsSaving(false));
    }

    return (

        <ValidatorForm onSubmit={submit} noValidate>

            <Grid2 container spacing={1}>


                <Grid2 size={{ xs: 12 }}>
                    <StandardTextValidator
                        label="Name"
                        value={data.name}
                        name="name"
                        required
                        fullWidth
                        validators={['required']}
                        errorMessages={['This field is required']}
                        onChange={handleChange} />
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <StandardTextValidator
                        label="Section Order"
                        value={data.sectionOrder}
                        name="sectionOrder"
                        required
                        fullWidth
                        validators={['required', 'minNumber:0', 'maxNumber:15']}
                        errorMessages={['This field is required', "Section order must be between 1 and 15", "Section order must be between 1 and 15"]}
                        onChange={handleChange}
                        min={1}
                        max={10}
                        step={1}
                        type="number" />
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <EnumSelect
                        key="formTemplateSectionTypeId"
                        enumObject={FormTemplateSectionTypeEnum}
                        label="Section Type"
                        name="formTemplateSectionTypeId"
                        value={data.formTemplateSectionTypeId}
                        onChange={handleChange}
                        required
                        fullWidth
                    />

                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                    <FormControlLabel
                        control={<Switch checked={data.hasFormFieldConditionDependency || false} onChange={handleChangeSwitch} name="hasFormFieldConditionDependency" />}
                        label="Has Form Field Condition Dependency?"
                    />
                </Grid2>

                {(data.hasFormFieldConditionDependency === true) &&
                    <>
                        <Grid2 size={{ xs: 12 }}>
                            <EnumSelect
                                enumObject={FormFieldKeyConditionTypeEnum}
                                label="Condition Type"
                                name="formFieldKeyConditionTypeId"
                                value={Number(data.formFieldKeyConditionTypeId || 0)}
                                onChange={handleChange}
                                required />
                        </Grid2>

                        <Grid2 size={{ xs: 12 }}>
                            <StandardButton
                                type="button"
                                icon="check"
                                onClick={() => { setData((_data: any) => ({ ..._data, formFieldKeyConditionDependencies: [...(_data.formFieldKeyConditionDependencies || []), { formTemplateSectionId: 0, conditionId: 0, value: "" }] })) }} >
                                Add Another Condition
                            </StandardButton>
                        </Grid2>

                        {(data.formFieldKeyConditionDependencies && data.formFieldKeyConditionDependencies.length > 0) && data.formFieldKeyConditionDependencies.map((d: any, index: number) =>
                            <>
                                <Grid2 size={{ xs: 4 }}>
                                    <DataTypeAutoComplete
                                        dataType={DataTypeEnum.FormFieldKey}
                                        label="Form Field Key Dependency"
                                        value={data.formFieldKeyConditionDependencies?.[index]?.conditionFormFieldKeyId}
                                        name={"conditionFormFieldKeyId"}
                                        onChange={(event: any) => { setData((_data: any) => { { var formFieldKeyConditionDependencies = [...(_data.formFieldKeyConditionDependencies || [])]; formFieldKeyConditionDependencies[index].conditionFormFieldKeyId = event.target.value; return { ..._data, formFieldKeyConditionDependencies: formFieldKeyConditionDependencies } } }) }}
                                        parentId={+(formTemplateSectionId || 0)}
                                        hasParent
                                        required />
                                </Grid2>

                                <Grid2 size={{ xs: 3 }}>
                                    <SelectValidator
                                        value={data.formFieldKeyConditionDependencies?.[index]?.formFieldKeyConditionId}
                                        onChange={(event) => { setData((_data: any) => { { var formFieldKeyConditionDependencies = [...(_data.formFieldKeyConditionDependencies || [])]; formFieldKeyConditionDependencies[index].formFieldKeyConditionId = (event.target as any).value; return { ..._data, formFieldKeyConditionDependencies: formFieldKeyConditionDependencies } } }) }}
                                        required
                                        label="Select Condition"
                                        name="formFieldKeyConditionId"
                                        fullWidth
                                        validators={['required']}
                                        errorMessages={['This field is required']}
                                        InputLabelProps={{
                                            shrink: !!data.formFieldKeyConditionDependencies?.[index]?.formFieldKeyConditionId,
                                        }}>
                                        <MenuItem value={FormFieldKeyConditionEnum.EqualsTo.toString()}>{FormFieldKeyConditionEnum[FormFieldKeyConditionEnum.EqualsTo]}</MenuItem>
                                        <MenuItem value={FormFieldKeyConditionEnum.EqualsOrGreater.toString()}>{FormFieldKeyConditionEnum[FormFieldKeyConditionEnum.EqualsOrGreater]}</MenuItem>
                                        <MenuItem value={FormFieldKeyConditionEnum.EqualsOrLess.toString()}>{FormFieldKeyConditionEnum[FormFieldKeyConditionEnum.EqualsOrLess]}</MenuItem>
                                        <MenuItem value={FormFieldKeyConditionEnum.Greater.toString()}>{FormFieldKeyConditionEnum[FormFieldKeyConditionEnum.Greater]}</MenuItem>
                                        <MenuItem value={FormFieldKeyConditionEnum.Less.toString()}>{FormFieldKeyConditionEnum[FormFieldKeyConditionEnum.Less]}</MenuItem>
                                        <MenuItem value={FormFieldKeyConditionEnum.Contains.toString()}>{FormFieldKeyConditionEnum[FormFieldKeyConditionEnum.Contains]}</MenuItem>
                                    </SelectValidator>
                                </Grid2>
                                <Grid2 size={{ xs: 3 }}>
                                    <StandardTextValidator
                                        label="Value"
                                        value={data.formFieldKeyConditionDependencies?.[index]?.value}
                                        name="value"
                                        required
                                        fullWidth
                                        validators={['required']}
                                        errorMessages={['This field is required']}
                                        onChange={(event: any) => { setData((_data: any) => { { var formFieldKeyConditionDependencies = [...(_data.formFieldKeyConditionDependencies || [])]; formFieldKeyConditionDependencies[index].value = (event.target as any).value; return { ..._data, formFieldKeyConditionDependencies: formFieldKeyConditionDependencies } } }) }}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 2 }}>
                                    <StandardButton
                                        type="button"
                                        icon="clear"
                                        loading={isLoading}
                                        saving={saving}
                                        onClick={() => { setData((_data: any) => { const deps = [...(_data.formFieldKeyConditionDependencies || [])]; deps.splice(index, 1); return { ..._data, formFieldKeyConditionDependencies: deps }; }); }} >
                                        Remove
                                    </StandardButton>
                                </Grid2>
                            </>
                        )}
                    </>
                }

                <Grid2 size={{ xs: 6 }}>
                    <StandardButton loading={isLoading} saving={saving} type="submit" icon="save">Save</StandardButton>
                </Grid2>

                {(data.formTemplateId > 0) &&
                    <Grid2 size={{ xs: 6 }}>
                        <StandardButton saving={saving} loading={isLoading} onClick={() => { navigate(-1) }} type="button" variant="warning" icon="back">Back</StandardButton>
                    </Grid2>
                }

            </Grid2>
        </ValidatorForm >
    );
}